import React from 'react'
import { ConfirmationModalGenericProps } from '../MoveStorageItemModal/useConfirmationModal'
import { Modal, Button } from '@sketch/components'
import {
  StyledSeparator,
  InfoBlock,
} from './ConfirmStorageItemTransferModal.styles'
import {
  StorageItemFragment,
  WorkspaceMinimalFragment,
  PublicWorkspaceFragment,
  ProjectFragment,
} from '@sketch/gql-types'
import { StorageItemAccessList } from '../../components/StorageItemAccessList'
import { ReactComponent as InfoIcon } from '@sketch/icons/circle-information-button-translucent-17'

type Project = Pick<
  ProjectFragment,
  | 'identifier'
  | 'name'
  | 'workspaceAccessLevel'
  | 'projectType'
  | 'topLevelProject'
>

export interface ConfirmStorageItemTransferModalProps
  extends ConfirmationModalGenericProps {
  item: StorageItemFragment | ProjectFragment
  destination: {
    project: Project | null
    workspace: WorkspaceMinimalFragment
  }
  currentLocation: {
    project: Project | null
    workspace: WorkspaceMinimalFragment | PublicWorkspaceFragment
  }
}

interface DescriptionProps
  extends Pick<
    ConfirmStorageItemTransferModalProps,
    'currentLocation' | 'destination' | 'item'
  > {
  isTheSameWorkspace: boolean
}
const Description = (props: DescriptionProps) => {
  const { currentLocation, destination, item, isTheSameWorkspace } = props
  const currentTopLevelProject =
    currentLocation.project?.topLevelProject || currentLocation.project || null
  const destinationTopLevelProject =
    destination.project?.topLevelProject || destination.project || null

  const itemType = item.__typename === 'Project' ? 'folder' : 'document'

  const isDifferentTopLevelProject =
    currentTopLevelProject?.identifier !==
    destinationTopLevelProject?.identifier

  const isMovingToNestedProject = !!destination.project?.topLevelProject

  if (isTheSameWorkspace && isDifferentTopLevelProject) {
    const potentialPermissionLossWarning = `Some people may lose access to this ${itemType}. You may want to review its permissions after moving.`
    if (isMovingToNestedProject) {
      return (
        <div>
          <b>{item.name}</b> will move into <b>{destination.project?.name}</b>{' '}
          and use the same permissions as{' '}
          <b>{destinationTopLevelProject?.name}</b>.{' '}
          {potentialPermissionLossWarning}
        </div>
      )
    } else {
      return (
        <div>
          <b>{item.name}</b> will move into{' '}
          <b>{destination.project?.name || destination.workspace.name}</b>.{' '}
          {potentialPermissionLossWarning}
        </div>
      )
    }
  }

  return (
    <>
      <div>
        Moving <b>{item.name}</b> and all its contents to{' '}
        {destination.project ? (
          <>
            <b>{destination.project.name}</b> of the{' '}
          </>
        ) : null}{' '}
        <b>{destination.workspace.name}</b> may affect access for some people.
        Review its permissions after moving.
      </div>

      <InfoBlock>
        <span>
          <InfoIcon width={17} height={17} />
        </span>
        <span>
          When this {itemType} has moved, only an admin of{' '}
          <b>{destination.workspace.name}</b> will be able to
          move&nbsp;it&nbsp;again.
        </span>
      </InfoBlock>
    </>
  )
}

export const ConfirmStorageItemTransferModal = (
  props: ConfirmStorageItemTransferModalProps
) => {
  const {
    onCancel,
    onConfirm,
    hideModal,
    loading,
    currentLocation,
    destination,
    item,
  } = props

  const isTheSameWorkspace =
    currentLocation?.workspace?.identifier === destination.workspace.identifier

  const itemType = item.__typename === 'Project' ? 'folder' : 'document'

  const onConfirmLoadingRef = React.useRef(false)

  return (
    <Modal onCancel={onCancel}>
      <Modal.Header>Move {itemType}?</Modal.Header>

      <Modal.Body>
        <Description
          item={item}
          currentLocation={currentLocation}
          destination={destination}
          isTheSameWorkspace={isTheSameWorkspace}
        />
        <StyledSeparator />
        <StorageItemAccessList
          destination={destination}
          item={item}
          hideSharePermissions={!isTheSameWorkspace}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onCancel}>Go Back</Button>
        <Button
          variant="primary"
          loading={loading}
          onClick={async () => {
            if (onConfirmLoadingRef.current) return
            onConfirmLoadingRef.current = true
            try {
              await onConfirm(hideModal)
            } finally {
              onConfirmLoadingRef.current = false
            }
          }}
        >
          Move
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
